import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import LocalStorage from '@utils-clean/local-storage-service';
import { LocalContextPersistedKey } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { SystemOrganisationKeyNameFields } from '@queries/generated/fragments.generated';
import './organisation-selector.scss';

interface OrganisationSelectorProps {
    disabled: boolean;
    hideBorder: boolean;
    systemOrgs: SystemOrganisationKeyNameFields[];
    systemOrgsLoading: boolean;
    selectOrganisation: (organisationKey: string) => void;
    organisationKey: string;
}

const OrganisationSelector = ({
    disabled,
    hideBorder,
    systemOrgs,
    systemOrgsLoading,
    selectOrganisation,
    organisationKey,
}: OrganisationSelectorProps) => {
    const { user } = useContext(UserContext);
    const lockedToOrgKey = sessionStorage.getItem(LocalContextPersistedKey.LockedToOrgKey);

    // Select an org from the system orgs list that matches the current user's org key, unless the page is locked to an organisation
    // that differs from the user's currently selected organisation.
    const selectedOrg =
        !lockedToOrgKey || user?.organisation.key === lockedToOrgKey
            ? systemOrgs.find((item) => organisationKey === item.key)
            : undefined;

    return (
        <div
            className="k-display-flex k-align-items-center k-w-full organisation-selector"
            style={{ columnGap: '10px', maxWidth: '292px' }}
        >
            <FontAwesomeIcon icon={['far', 'sitemap']} size="xl" className="k-color-white" />
            <BasicDropDown
                disabled={disabled}
                readOnly={IsPopOut()}
                className="k-h-8 k-w-full navbar-dropdown"
                data-testid="select-organisation-in-focus"
                data={systemOrgs}
                loading={systemOrgsLoading}
                value={selectedOrg}
                textField="name"
                hideBorder={hideBorder}
                onChange={(e) => {
                    const newlySelectedOrgKey = (e.value as SystemOrganisationKeyNameFields).key as string;
                    selectOrganisation(newlySelectedOrgKey);

                    // This is used by any child popped out pages so they compare their org key with this parent page.
                    LocalStorage.setItem(user!, 'ParentPageOrgKey', newlySelectedOrgKey);
                }}
                customClassName="navbar-popup"
                filterable
            />
        </div>
    );
};

export default OrganisationSelector;
